import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { TaskFinancialDocument } from "@common/types/task";
import {
    ViewTable,
    type ViewTableRowDragOut,
} from "@design-system/ViewTable/ViewTable";
import { formatDocumentPeriod } from "@components/documents/formatDocumentPeriod";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { pluralize } from "@common/helpers/string";
import { useDocumentRowHighlight } from "../../useDocumentRowHighlight";
import type { useDocumentRetrievalTaskModel } from "./useDocumentRetrievalTaskModel";
import {
    documentRetrievalDefaultView,
    documentRetrievalViewConfig,
    type DocumentRetrievalTableMeta,
    type DocumentRetrievalTableRow,
} from "./documentRetrievalViewConfig";
import styles from "./DocumentRetrievalTaskBody.module.scss";

type DocumentRetrievalTaskModel = ReturnType<
    typeof useDocumentRetrievalTaskModel
>;

interface Props {
    model: DocumentRetrievalTaskModel;
    rowSelection: RowSelectionState;
    onRowSelectionChange: (
        selection: RowSelectionState,
        selected: { documentIds: number[]; monthKeys: string[] },
    ) => void;
    footer?: ReactNode;
    rowDragOut?: ViewTableRowDragOut<DocumentRetrievalTableRow>;
}

function documentRowExtras(
    doc: TaskFinancialDocument | undefined,
): Pick<DocumentRetrievalTableRow, "statementPeriod" | "accountNumber"> {
    if (!doc) {
        return {
            statementPeriod: null,
            accountNumber: null,
        };
    }

    return {
        statementPeriod: formatDocumentPeriod(doc.taxonomyExtraction),
        accountNumber: doc.taxonomyExtraction?.accountNumber ?? null,
    };
}

const UNVERIFIED_SEPARATOR_ROW: DocumentRetrievalTableRow = {
    id: "unverified-separator",
    kind: "separator",
    rowNumber: null,
    periodLabel: null,
    fileName: null,
    status: null,
    exclusionReason: null,
    monthKey: null,
    documentId: null,
    verification: null,
    statementPeriod: null,
    accountNumber: null,
};

function selectedRowsFromSelection(
    selection: RowSelectionState,
    rows: DocumentRetrievalTableRow[],
): { documentIds: number[]; monthKeys: string[] } {
    const documentIds: number[] = [];
    const monthKeys: string[] = [];
    for (const row of rows) {
        if (!selection[row.id]) {
            continue;
        }
        if (row.documentId != null) {
            documentIds.push(row.documentId);
        }
        if (row.monthKey != null && row.status !== "verified") {
            monthKeys.push(row.monthKey);
        }
    }
    return { documentIds, monthKeys };
}

function canSelectDocumentRetrievalRow(
    row: DocumentRetrievalTableRow,
): boolean {
    return row.kind !== "separator";
}

function renderUnverifiedSeparator(
    row: DocumentRetrievalTableRow,
    unverifiedCount: number,
): ReactNode | null {
    if (row.kind !== "separator") {
        return null;
    }

    return (
        <span className={styles.unverifiedSection}>
            Invalid {pluralize("document", unverifiedCount)} ({unverifiedCount})
        </span>
    );
}

export function DocumentRetrievalViewTables({
    model,
    rowSelection,
    onRowSelectionChange,
    footer,
    rowDragOut,
}: Readonly<Props>) {
    const { open: openUpload } = useFinancialDocumentUpload();
    const {
        rows,
        unverifiedDocs,
        rawDocs,
        handleExclude,
        handleClearExclusion,
        handleRetry,
        openPreview,
        handleRemoveFromTask,
        removingDocumentIds,
    } = model;

    const docsById = useMemo(
        () => new Map(rawDocs.map((doc) => [doc.id, doc])),
        [rawDocs],
    );

    const tableRows = useMemo<DocumentRetrievalTableRow[]>(() => {
        const monthRows: DocumentRetrievalTableRow[] = rows.map(
            (row, index) => {
                const extras = documentRowExtras(
                    row.documentId == null
                        ? undefined
                        : docsById.get(row.documentId),
                );
                return {
                    id: row.key,
                    kind: "month",
                    rowNumber: index + 1,
                    periodLabel: row.label,
                    fileName: row.fileName,
                    status: row.status,
                    exclusionReason: row.exclusionReason,
                    monthKey: row.key,
                    documentId: row.documentId,
                    verification: null,
                    ...extras,
                };
            },
        );

        const extraRows: DocumentRetrievalTableRow[] = unverifiedDocs.map(
            (doc) => {
                const extras = documentRowExtras(docsById.get(doc.id));
                return {
                    id: `unverified-${doc.id}`,
                    kind: "unverified",
                    rowNumber: null,
                    periodLabel: doc.period,
                    fileName: doc.fileName,
                    status: null,
                    exclusionReason: null,
                    monthKey: null,
                    documentId: doc.id,
                    verification: doc.verification,
                    ...extras,
                };
            },
        );

        if (extraRows.length === 0) {
            return monthRows;
        }

        return [...monthRows, UNVERIFIED_SEPARATOR_ROW, ...extraRows];
    }, [rows, unverifiedDocs, docsById]);

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            const nextSelection =
                typeof updater === "function" ? updater(rowSelection) : updater;
            onRowSelectionChange(
                nextSelection,
                selectedRowsFromSelection(nextSelection, tableRows),
            );
        },
        [onRowSelectionChange, rowSelection, tableRows],
    );

    const reportedSelectionRef = useRef(
        JSON.stringify({ selection: [], documentIds: [], monthKeys: [] }),
    );
    useEffect(
        function reconcileSelectionWithRows() {
            const selectableRowIds = new Set(
                tableRows
                    .filter((row) => row.kind !== "separator")
                    .map((row) => row.id),
            );
            const prunedSelection: RowSelectionState = {};
            for (const id of Object.keys(rowSelection)) {
                if (rowSelection[id] && selectableRowIds.has(id)) {
                    prunedSelection[id] = true;
                }
            }
            const selected = selectedRowsFromSelection(
                prunedSelection,
                tableRows,
            );
            const signature = JSON.stringify({
                selection: Object.keys(prunedSelection).sort(),
                documentIds: [...selected.documentIds].sort((a, b) => a - b),
                monthKeys: [...selected.monthKeys].sort(),
            });
            if (signature === reportedSelectionRef.current) {
                return;
            }
            reportedSelectionRef.current = signature;
            onRowSelectionChange(prunedSelection, selected);
        },
        [tableRows, rowSelection, onRowSelectionChange],
    );

    const resolveTableRowId = useCallback(
        (documentId: number) =>
            tableRows.find((row) => row.documentId === documentId)?.id,
        [tableRows],
    );
    const { viewTableRef, selectedRowId } = useDocumentRowHighlight({
        resolveTableRowId,
    });

    const tableMeta = useMemo<DocumentRetrievalTableMeta>(
        () => ({
            callbacks: {
                onExclude: handleExclude,
                onClearExclusion: handleClearExclusion,
                onReview: openPreview,
                onRetry: handleRetry,
                onUpload: openUpload,
                onRemoveFromTask: handleRemoveFromTask,
                isRemovingDocument: (documentId: number) =>
                    removingDocumentIds.includes(documentId),
            },
        }),
        [
            handleExclude,
            handleClearExclusion,
            openPreview,
            handleRetry,
            openUpload,
            handleRemoveFromTask,
            removingDocumentIds,
        ],
    );

    return (
        <ViewTable
            ref={viewTableRef}
            data={tableRows}
            view={documentRetrievalDefaultView}
            config={documentRetrievalViewConfig}
            selectedRowId={selectedRowId}
            getRowId={(row) => row.id}
            onRowClick={(row) => {
                if (row?.documentId != null) {
                    openPreview(row.documentId);
                }
            }}
            renderFullWidthRow={(row) =>
                renderUnverifiedSeparator(row, unverifiedDocs.length)
            }
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            enableRowSelection={canSelectDocumentRetrievalRow}
            tableMeta={tableMeta}
            outerBorder={false}
            enableColumnResizing
            enableKeyboardShortcuts
            className={styles.viewTable}
            fixedTopOffsetPx
            footer={footer}
            rowDragOut={rowDragOut}
        />
    );
}
